/**
 * The open-source notice screen (§40).
 *
 * Required for release on both stores, and worth having anyway: a viewer that
 * asks people to trust it with their documents should be able to say what it
 * is made of.
 *
 * The list is generated from the installed dependency tree, not from the
 * catalog's plans — `tools/generate-notices.mjs` writes it, and `npm run
 * check` fails when it drifts. The full licence texts live in `NOTICE.md`
 * rather than here: tens of kilobytes of legal prose in the renderer bundle
 * would slow the first paint of every document for a screen almost nobody
 * opens (§33).
 */
import { useMemo, useState } from 'react';
import { Icon } from '@moduviewer/design/icon';
import { LICENSE_COUNTS, NOTICES } from '../generated/notices.js';

export interface NoticesPanelProps {
  readonly onClose: () => void;
}

export function NoticesPanel({ onClose }: NoticesPanelProps): React.JSX.Element {
  const [filter, setFilter] = useState('');

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return NOTICES;
    return NOTICES.filter((n) =>
      n.name.toLowerCase().includes(needle) || n.license.toLowerCase().includes(needle));
  }, [filter]);

  return (
    <aside className="panel notices" aria-labelledby="notices-title">
      <header className="panel__head">
        <h2 className="panel__title" id="notices-title">오픈소스 고지</h2>
        <button className="btn btn--icon" aria-label="닫기" onClick={onClose}>
          <Icon name="close" />
        </button>
      </header>

      <p className="notices__lead">
        모두뷰어는 다음 오픈소스 소프트웨어를 포함합니다. 각 라이선스 전문은
        배포본에 함께 담긴 <code>NOTICE.md</code> 에 그대로 실려 있습니다.
      </p>

      <ul className="notices__summary">
        {LICENSE_COUNTS.map(([license, count]) => (
          <li key={license}>
            <span className="badge">{license}</span>
            <span className="tnum">{count}</span>
          </li>
        ))}
      </ul>

      <label className="visually-hidden" htmlFor="notice-filter">패키지 검색</label>
      <input
        id="notice-filter"
        className="field"
        placeholder="패키지 또는 라이선스"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <ul className="notices__list">
        {shown.map((notice) => (
          <li key={`${notice.name}@${notice.version}`} className="notice-row">
            <span className="notice-row__name truncate">{notice.name}</span>
            <span className="notice-row__version tnum">{notice.version}</span>
            <span className="notice-row__license">{notice.license}</span>
          </li>
        ))}
      </ul>

      {shown.length === 0 ? (
        <p className="notices__empty">일치하는 패키지가 없습니다.</p>
      ) : null}

      <p className="notices__count">
        {shown.length === NOTICES.length
          ? `총 ${NOTICES.length}개 패키지`
          : `${NOTICES.length}개 중 ${shown.length}개`}
      </p>
    </aside>
  );
}
